import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '@/lib/logger';
import { Play, Pause, SkipForward, ArrowLeft, Check, Warning, Fire, Clock, Barbell, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUDIO_PREF_KEY = 'gritfit_audio_cues';

// Haptic feedback helper (safe on non-supporting devices)
const vibrate = (pattern) => {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

// Text-to-speech helper (SpeechSynthesis is built-in, no API key needed)
const speak = (text, enabled) => {
  if (!enabled) return;
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1.1;
    utter.volume = 0.9;
    utter.pitch = 1.0;
    window.speechSynthesis.speak(utter);
  } catch (e) {
    logger.warn('speech error', e?.message);
  }
};

export default function WorkoutPlayer() {
  const location = useLocation();
  const navigate = useNavigate();
  const exercises = useMemo(() => location.state?.exercises || [], [location.state]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('ready');
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [currentSet, setCurrentSet] = useState(1);
  const [totalTime, setTotalTime] = useState(0);
  const [completedExercises, setCompletedExercises] = useState(0);
  const [swipeHint, setSwipeHint] = useState(null);
  const [isLandscape, setIsLandscape] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = window.localStorage.getItem(AUDIO_PREF_KEY);
    return saved === null ? true : saved === 'true';
  });

  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const audioEnabledRef = useRef(audioEnabled);

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUDIO_PREF_KEY, String(audioEnabled));
    }
  }, [audioEnabled]);

  // Use refs for values accessed inside setInterval to avoid stale closures
  const phaseRef = useRef(phase);
  const currentSetRef = useRef(currentSet);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { currentSetRef.current = currentSet; }, [currentSet]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const currentExercise = exercises[currentIndex];
  const currentExerciseRef = useRef(currentExercise);
  useEffect(() => { currentExerciseRef.current = currentExercise; }, [currentExercise]);

  useEffect(() => {
    if (!exercises.length) navigate('/workouts');
  }, [exercises, navigate]);

  // Detect landscape orientation (mobile only — desktop is always wide)
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Mobile landscape: width > height AND shorter side < 600px (so desktop doesn't trigger)
      setIsLandscape(w > h && Math.min(w, h) < 600);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // Screen Wake Lock
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          logger.info('Screen Wake Lock active');
        }
      } catch (err) {
        logger.warn('Wake Lock request failed:', err?.message);
      }
    };

    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      // Stop any in-flight speech
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const moveToNext = useCallback(() => {
    setCompletedExercises(prev => prev + 1);
    if (currentIndexRef.current < exercises.length - 1) {
      const nextEx = exercises[currentIndexRef.current + 1];
      setCurrentIndex(prev => prev + 1);
      setCurrentSet(1);
      setPhase('ready');
      setTimer(0);
      setIsRunning(false);
      vibrate([80, 40, 80]);
      speak(`Next up: ${nextEx?.name || 'Next exercise'}`, audioEnabledRef.current);
    } else {
      setPhase('complete');
      setIsRunning(false);
      clearInterval(intervalRef.current);
      vibrate([200, 100, 200, 100, 400]);
      speak('Workout complete! Great work.', audioEnabledRef.current);
      axios.post(`${API}/workouts/complete`, {
        workout_id: exercises.map(e => e.id || e.name).join('-').substring(0, 100),
        duration_minutes: Math.max(1, Math.ceil(totalTime / 60)),
        exercises_completed: exercises.length,
      }).catch(err => logger.error('Complete workout error:', err));
    }
  }, [exercises, totalTime]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (phaseRef.current === 'rest' && prev <= 1) {
            clearInterval(intervalRef.current);
            setIsRunning(false);
            vibrate([150, 60, 150]);
            const maxSets = currentExerciseRef.current?.sets || 3;
            if (currentSetRef.current < maxSets) {
              setCurrentSet(s => s + 1);
              setPhase('exercise');
              speak('Go!', audioEnabledRef.current);
              return 0;
            } else {
              moveToNext();
              return 0;
            }
          }
          // 3-2-1 countdown during final seconds of rest
          if (phaseRef.current === 'rest' && prev > 1 && prev <= 4) {
            vibrate(50);
            if (prev === 4) speak('3', audioEnabledRef.current);
            if (prev === 3) speak('2', audioEnabledRef.current);
            if (prev === 2) speak('1', audioEnabledRef.current);
          }
          return phaseRef.current === 'rest' ? prev - 1 : prev + 1;
        });
        setTotalTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, moveToNext]);

  const startExercise = () => {
    vibrate(60);
    setPhase('exercise');
    setTimer(0);
    setIsRunning(true);
    speak(`${currentExercise?.name || 'Exercise'}. Go!`, audioEnabledRef.current);
  };

  const finishSet = () => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    vibrate([100, 50, 100]);
    if (currentSet < (currentExercise?.sets || 3)) {
      setPhase('rest');
      setTimer(currentExercise?.rest_seconds || 45);
      speak('Set complete. Rest.', audioEnabledRef.current);
      setTimeout(() => setIsRunning(true), 100);
    } else {
      moveToNext();
    }
  };

  const skipRest = () => {
    clearInterval(intervalRef.current);
    setIsRunning(false);
    setTimer(0);
    vibrate(40);
    if (currentSet < (currentExercise?.sets || 3)) {
      setCurrentSet(s => s + 1);
      setPhase('exercise');
    } else {
      moveToNext();
    }
  };

  const goPrevExercise = useCallback(() => {
    if (currentIndex > 0) {
      clearInterval(intervalRef.current);
      setCurrentIndex(i => i - 1);
      setCurrentSet(1);
      setPhase('ready');
      setTimer(0);
      setIsRunning(false);
      vibrate(40);
    }
  }, [currentIndex]);

  const skipExercise = useCallback(() => {
    clearInterval(intervalRef.current);
    vibrate(40);
    moveToNext();
  }, [moveToNext]);

  const toggleAudio = () => {
    setAudioEnabled(prev => {
      const next = !prev;
      if (!next && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      return next;
    });
    vibrate(30);
  };

  // Swipe gesture handlers
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    if (Math.abs(dx) > 60 && Math.abs(dy) < 50 && dt < 600) {
      if (dx < 0) {
        setSwipeHint('left');
        skipExercise();
      } else {
        setSwipeHint('right');
        goPrevExercise();
      }
      setTimeout(() => setSwipeHint(null), 350);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  if (!exercises.length) return null;

  if (phase === 'complete') {
    const calorieEstimate = exercises.reduce((acc, ex) => acc + (ex.calories_per_set || 7) * (ex.sets || 3), 0);
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="workout-complete">
        <div className="text-center max-w-md mx-auto w-full">
          <div className="w-20 h-20 bg-[#34C759]/10 border border-[#34C759]/30 flex items-center justify-center mx-auto mb-6">
            <Check weight="bold" className="w-10 h-10 text-[#34C759]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-3">WORKOUT COMPLETE</h1>
          <p className="text-[#34C759] font-bold text-lg mb-8">Your streak has been updated!</p>

          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="tactical-card p-4">
              <Barbell weight="bold" className="w-6 h-6 text-[#FF3B30] mx-auto mb-2" />
              <div className="text-2xl font-black font-['Barlow_Condensed']">{exercises.length}</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider">Exercises</div>
            </div>
            <div className="tactical-card p-4">
              <Clock weight="bold" className="w-6 h-6 text-[#007AFF] mx-auto mb-2" />
              <div className="text-2xl font-black font-['Barlow_Condensed']">{formatTime(totalTime)}</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider">Duration</div>
            </div>
            <div className="tactical-card p-4">
              <Fire weight="bold" className="w-6 h-6 text-[#FF9500] mx-auto mb-2" />
              <div className="text-2xl font-black font-['Barlow_Condensed']">~{calorieEstimate}</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider">Calories</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/dashboard')} className="btn-primary px-8 py-4 min-h-[56px] text-lg" data-testid="back-to-dashboard">
              Dashboard
            </button>
            <button onClick={() => navigate('/workouts')} className="btn-secondary px-8 py-4 min-h-[56px] text-lg">
              More Workouts
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalSets = currentExercise?.sets || 3;

  // ============== LANDSCAPE LAYOUT (mobile rotated) ==============
  if (isLandscape) {
    return (
      <div
        data-testid="workout-player-page"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className={`select-none transition-transform duration-200 min-h-screen -m-4 sm:-m-6 p-3 ${swipeHint === 'left' ? '-translate-x-2' : ''} ${swipeHint === 'right' ? 'translate-x-2' : ''}`}
      >
        <div className="grid grid-cols-2 gap-4 h-[calc(100vh-1rem)]">
          {/* Left: Timer + exercise name */}
          <div className="flex flex-col justify-center items-center tactical-card p-4">
            <div className="label-tactical text-[#FF3B30] mb-1">Ex {currentIndex + 1}/{exercises.length} · Set {currentSet}/{totalSets}</div>
            <h2 className="text-2xl font-black tracking-tighter mb-2 text-center line-clamp-2">{currentExercise?.name}</h2>
            <div className="text-[#71717A] text-xs mb-2">{currentExercise?.reps}</div>
            <div className="text-6xl font-black font-['Barlow_Condensed'] tabular-nums" data-testid="workout-timer">
              <span className={phase === 'rest' ? 'text-[#007AFF]' : 'text-white'}>{formatTime(timer)}</span>
            </div>
            {phase === 'rest' && <div className="text-[#007AFF] font-bold text-xs uppercase tracking-widest mt-1">Rest</div>}
          </div>

          {/* Right: Controls */}
          <div className="flex flex-col justify-center gap-3">
            {phase === 'ready' && (
              <button onClick={startExercise} className="btn-primary py-4 text-lg flex items-center justify-center gap-2 min-h-[64px]" data-testid="start-exercise-button">
                <Play weight="fill" className="w-6 h-6" /> Start Set
              </button>
            )}
            {phase === 'exercise' && (
              <>
                <button onClick={finishSet} className="btn-primary py-4 text-lg flex items-center justify-center gap-2 min-h-[64px]" data-testid="finish-set-button">
                  <Check weight="bold" className="w-6 h-6" /> Done
                </button>
                <button onClick={() => { setIsRunning(!isRunning); vibrate(30); }} className="btn-secondary py-3 flex items-center justify-center min-h-[52px]" data-testid="pause-button" aria-label={isRunning ? 'Pause' : 'Resume'}>
                  {isRunning ? <Pause weight="fill" className="w-6 h-6" /> : <Play weight="fill" className="w-6 h-6" />}
                </button>
              </>
            )}
            {phase === 'rest' && (
              <button onClick={skipRest} className="btn-secondary py-4 text-lg flex items-center justify-center gap-2 min-h-[64px]" data-testid="skip-rest-button">
                <SkipForward weight="bold" className="w-5 h-5" /> Skip Rest
              </button>
            )}
            <div className="flex gap-2">
              <button onClick={() => navigate('/workouts')} className="btn-secondary flex-1 py-2 text-sm" data-testid="back-button" aria-label="Back">
                <ArrowLeft weight="bold" className="w-4 h-4 inline" /> Exit
              </button>
              <button
                onClick={toggleAudio}
                className="btn-secondary px-3 py-2"
                data-testid="audio-toggle-button"
                aria-label={audioEnabled ? 'Mute voice coach' : 'Enable voice coach'}
              >
                {audioEnabled ? <SpeakerHigh weight="bold" className="w-4 h-4" /> : <SpeakerSlash weight="bold" className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== PORTRAIT / DESKTOP LAYOUT ==============
  return (
    <div
      data-testid="workout-player-page"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={`select-none transition-transform duration-200 ${swipeHint === 'left' ? '-translate-x-2' : ''} ${swipeHint === 'right' ? 'translate-x-2' : ''}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/workouts')}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors min-h-[44px] min-w-[44px] -ml-2 px-2"
          data-testid="back-button"
        >
          <ArrowLeft weight="bold" className="w-5 h-5" /> <span className="hidden sm:inline">Back</span>
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleAudio}
            className="text-[#A1A1AA] hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            data-testid="audio-toggle-button"
            aria-label={audioEnabled ? 'Mute voice coach' : 'Enable voice coach'}
            title={audioEnabled ? 'Voice coach ON' : 'Voice coach OFF'}
          >
            {audioEnabled ? <SpeakerHigh weight="bold" className="w-5 h-5" /> : <SpeakerSlash weight="bold" className="w-5 h-5 text-[#71717A]" />}
          </button>
          <div className="text-right">
            <div className="label-tactical text-[#FF3B30]">Exercise {currentIndex + 1} of {exercises.length}</div>
            <div className="text-sm text-[#71717A]">Total: {formatTime(totalTime)}</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex gap-1 mb-6 sm:mb-8">
        {exercises.map((_, idx) => (
          <div key={`progress-${idx}`} className={`h-1.5 flex-1 transition-all duration-300 ${idx < currentIndex ? 'bg-[#34C759]' : idx === currentIndex ? 'bg-[#FF3B30]' : 'bg-white/10'}`} />
        ))}
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="tactical-card p-5 sm:p-8 mb-6">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter mb-2 break-words">{currentExercise?.name}</h2>
            <div className="label-tactical text-[#FF3B30] mb-6">
              Set {currentSet} of {totalSets} | {currentExercise?.reps}
            </div>

            <div className="flex justify-center gap-2 mb-6">
              {Array.from({ length: totalSets }).map((_, idx) => (
                <div key={`set-${idx}`} className={`w-3 h-3 transition-all ${idx < currentSet - 1 ? 'bg-[#34C759]' : idx === currentSet - 1 && phase === 'exercise' ? 'bg-[#FF3B30] pulse-glow' : 'bg-white/10'}`} />
              ))}
            </div>

            <p className="text-[#A1A1AA] mb-6 max-w-md mx-auto text-sm sm:text-base">{currentExercise?.description}</p>

            <div className="text-6xl sm:text-7xl md:text-8xl font-black font-['Barlow_Condensed'] mb-6 tabular-nums" data-testid="workout-timer">
              {phase === 'rest' ? (
                <span className="text-[#007AFF]">{formatTime(timer)}</span>
              ) : (
                <span>{formatTime(timer)}</span>
              )}
            </div>

            {phase === 'rest' && (
              <div className="text-[#007AFF] font-bold text-lg sm:text-xl mb-4 uppercase tracking-widest">Rest Period</div>
            )}

            <div className="flex justify-center gap-3 sm:gap-4 flex-wrap">
              {phase === 'ready' && (
                <button
                  onClick={startExercise}
                  className="btn-primary px-10 py-5 text-lg sm:text-xl flex items-center gap-2 pulse-glow min-h-[64px] w-full sm:w-auto"
                  data-testid="start-exercise-button"
                >
                  <Play weight="fill" className="w-7 h-7" /> Start Set
                </button>
              )}
              {phase === 'exercise' && (
                <>
                  <button
                    onClick={() => { setIsRunning(!isRunning); vibrate(30); }}
                    className="btn-secondary px-6 py-5 text-lg flex items-center justify-center min-h-[64px] min-w-[64px]"
                    data-testid="pause-button"
                    aria-label={isRunning ? 'Pause' : 'Resume'}
                  >
                    {isRunning ? <Pause weight="fill" className="w-7 h-7" /> : <Play weight="fill" className="w-7 h-7" />}
                  </button>
                  <button
                    onClick={finishSet}
                    className="btn-primary px-8 py-5 text-lg sm:text-xl flex items-center gap-2 min-h-[64px] flex-1 sm:flex-none justify-center"
                    data-testid="finish-set-button"
                  >
                    <Check weight="bold" className="w-7 h-7" /> Done
                  </button>
                </>
              )}
              {phase === 'rest' && (
                <button
                  onClick={skipRest}
                  className="btn-secondary px-8 py-5 text-lg flex items-center gap-2 min-h-[64px] w-full sm:w-auto justify-center"
                  data-testid="skip-rest-button"
                >
                  <SkipForward weight="bold" className="w-6 h-6" /> Skip Rest
                </button>
              )}
            </div>

            <div className="mt-5 text-[11px] text-[#52525B] uppercase tracking-widest sm:hidden">
              Swipe ← next · → previous · Rotate for compact view
            </div>
          </div>
        </div>

        <div className="hidden sm:flex items-center justify-between mb-6 text-sm">
          <button
            onClick={goPrevExercise}
            disabled={currentIndex === 0}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] px-3"
            data-testid="prev-exercise-button"
          >
            <ArrowLeft weight="bold" className="w-4 h-4" /> Previous
          </button>
          <button
            onClick={skipExercise}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors min-h-[44px] px-3"
            data-testid="skip-exercise-button"
          >
            Skip Exercise <SkipForward weight="bold" className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-24 md:pb-4">
          <div className="tactical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#34C759]/10 flex items-center justify-center">
                <Check weight="bold" className="w-3 h-3 text-[#34C759]" />
              </div>
              <span className="label-tactical text-[#34C759]">Form Tip</span>
            </div>
            <p className="text-sm text-[#A1A1AA]">{currentExercise?.form_tip}</p>
          </div>
          <div className="tactical-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-[#FF9500]/10 flex items-center justify-center">
                <Warning weight="bold" className="w-3 h-3 text-[#FF9500]" />
              </div>
              <span className="label-tactical text-[#FF9500]">Easier Modification</span>
            </div>
            <p className="text-sm text-[#A1A1AA]">{currentExercise?.modification}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
